import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GoogleMap, MarkerF } from "@react-google-maps/api";
import {
  TextField,
  Typography,
  List,
  ListItemText,
  ListItemButton,
  Box,
  InputAdornment,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import useMapLoader from "hooks/useMapLoader";
import colors from "config/theme/colors";
import { debounce } from "lodash";
import SearchIcon from "@mui/icons-material/Search";

const DEFAULT_CONTAINER_STYLE = {
  width: "100%",
  height: "400px",
};

const center = {
  lat: 37.98,
  lng: 23.73,
};

const CITY_COMPONENT_TYPES = [
  "locality",
  "postal_town",
  "administrative_area_level_3",
  "administrative_area_level_2",
  "administrative_area_level_1",
  "sublocality",
  "sublocality_level_1",
];

function AutocompleteMap({
  onMarkerChange,
  marker,
  error,
  readOnly,
  draggable = true,
  containerStyle,
  inputFieldLabel,
  required,
}) {
  const [inputValue, setInputValue] = useState(marker?.formattedAddress || "");
  const [predictions, setPredictions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const { isLoaded } = useMapLoader();
  const boxRef = useRef();
  const hasMarker = useMemo(
    () => Number.isFinite(marker?.lat) && Number.isFinite(marker?.lng),
    [marker?.lat, marker?.lng],
  );
  const zoom = useMemo(() => (hasMarker ? 18 : 15), [hasMarker]);
  const mapCenter = useMemo(
    () => (hasMarker ? { lat: marker.lat, lng: marker.lng } : center),
    [hasMarker, marker?.lat, marker?.lng],
  );

  const extractAddressComponents = (addressComponents) => {
    let city = "";
    let postalCode = "";

    addressComponents?.forEach((component) => {
      if (
        !city &&
        CITY_COMPONENT_TYPES.some((type) => component.types?.includes(type))
      ) {
        city = component.long_name;
      }
      if (!postalCode && component.types?.includes("postal_code")) {
        postalCode = component.long_name;
      }
    });

    return { city, postalCode };
  };

  const getLocationDataFromGeocoderResults = useCallback((results = []) => {
    const addressComponents = results.flatMap(
      (result) => result.address_components || [],
    );
    const { city, postalCode } = extractAddressComponents(addressComponents);
    const formattedAddress =
      results.find((result) => result.formatted_address)?.formatted_address ||
      "";

    return { formattedAddress, city, postalCode };
  }, []);

  const updateMarkerFromLocation = useCallback(
    ({ lat, lng, formattedAddress = "", city = "", postalCode = "" }) => {
      setInputValue(formattedAddress);
      setPredictions([]);

      onMarkerChange?.({
        formattedAddress,
        lat,
        lng,
        city,
        postalCode,
      });
    },
    [onMarkerChange],
  );

  const reverseGeocodeLocation = useCallback(
    ({ lat, lng }) => {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results?.[0]) {
          const { formattedAddress, city, postalCode } =
            getLocationDataFromGeocoderResults(results);

          updateMarkerFromLocation({
            lat,
            lng,
            formattedAddress,
            city,
            postalCode,
          });
          return;
        }

        updateMarkerFromLocation({ lat, lng });
      });
    },
    [getLocationDataFromGeocoderResults, updateMarkerFromLocation],
  );

  useEffect(() => {
    setInputValue(marker?.formattedAddress || "");
  }, [marker?.formattedAddress]);

  const onPlaceSelected = (placeId) => {
    const service = new window.google.maps.places.PlacesService(
      document.createElement("div"),
    );
    service.getDetails({ placeId }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        const { formatted_address, geometry, address_components } = place;
        const { city, postalCode } =
          extractAddressComponents(address_components);

        updateMarkerFromLocation({
          formattedAddress: formatted_address,
          lat: geometry?.location?.lat(),
          lng: geometry?.location?.lng(),
          city,
          postalCode,
        });
      }
    });
  };

  const fetchPredictions = useCallback((value) => {
    if (!value) {
      setPredictions([]);
      return;
    }

    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      { input: value, componentRestrictions: { country: "gr" } },
      (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setPredictions(results);
        } else {
          setPredictions([]);
        }
      },
    );
  }, []);

  const debouncedFetchPredictions = useMemo(
    () => debounce(fetchPredictions, 3000),
    [fetchPredictions],
  );

  useEffect(() => {
    return () => {
      debouncedFetchPredictions.cancel();
    };
  }, [debouncedFetchPredictions]);

  const handleInputChange = (event) => {
    const value = event.target.value;
    if (value === "" || value.includes?.(inputValue)) {
      setPredictions([]);
    }
    setInputValue(value);
    // debouncedFetchPredictions(value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.nativeEvent.stopImmediatePropagation();
      event.stopPropagation();
      debouncedFetchPredictions.cancel();
      fetchPredictions(inputValue);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsFocused(false);
    }, 150);
  };

  const handleMapClick = (event) => {
    if (readOnly) {
      return;
    }

    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    reverseGeocodeLocation({ lat, lng });
  };

  const handleMarkerDragEnd = (event) => {
    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    reverseGeocodeLocation({ lat, lng });
  };

  const handleLocationFieldChange = (fieldName) => (event) => {
    if (!hasMarker) {
      return;
    }

    onMarkerChange?.({
      formattedAddress: marker?.formattedAddress || inputValue || "",
      lat: marker?.lat,
      lng: marker?.lng,
      city: fieldName === "city" ? event.target.value : marker?.city || "",
      postalCode:
        fieldName === "postalCode"
          ? event.target.value
          : marker?.postalCode || "",
    });
  };

  const mapOptions = {
    disableDefaultUI: true,
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "transit",
        stylers: [{ visibility: "off" }],
      },
    ],
  };

  return (
    <div className="relative">
      {isLoaded && (
        <>
          <div>
            <Typography
              fontSize={12}
              fontWeight={700}
              display="flex"
              alignItems="center"
              gap={1}
            >
              {inputFieldLabel} {required && "*"}
            </Typography>
            <Typography fontSize={12} color={colors.text} mt={0.5}>
              Αναζητήστε διεύθυνση ή τοποθετήστε την πινέζα με κλικ στον χάρτη.
            </Typography>

            <div className="mt-3" style={{ position: "relative" }}>
              <TextField
                placeholder="Επιλέξτε τοποθεσία στο χάρτη"
                fullWidth
                error={!!error}
                value={inputValue}
                disabled={readOnly}
                sx={{
                  backgroundColor: readOnly
                    ? colors.disabledInputBackground
                    : undefined,
                  "& .Mui-disabled": {
                    color: colors.text,
                    "-webkit-text-fill-color": colors.text,
                  },
                }}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={(e) => handleKeyDown(e)}
                helperText={error ? "Το πεδίο είναι υποχρεωτικό" : null}
                InputProps={{
                  readOnly: readOnly,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          debouncedFetchPredictions.cancel();
                          fetchPredictions(inputValue);
                        }}
                        edge="end"
                        disabled={readOnly}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  readOnly: readOnly,
                }}
              />
              {predictions.length > 0 && isFocused && (
                <Box
                  ref={boxRef}
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    backgroundColor: "white",
                    boxShadow: 1,
                    maxHeight: 200,
                    overflow: "auto",
                    borderRadius: 2,
                  }}
                >
                  <List component="nav" aria-label="predictions">
                    {predictions.map((prediction) => (
                      <ListItemButton
                        key={prediction.place_id}
                        onClick={() => onPlaceSelected(prediction.place_id)}
                      >
                        <ListItemText primary={prediction.description} />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              )}

              {hasMarker && !readOnly && (
                <Box
                  mt={2}
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
                  gap={2}
                >
                  <TextField
                    label="Πόλη"
                    value={marker?.city || ""}
                    onChange={handleLocationFieldChange("city")}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Τ.Κ."
                    value={marker?.postalCode || ""}
                    onChange={handleLocationFieldChange("postalCode")}
                    required
                    fullWidth
                  />
                </Box>
              )}
            </div>
          </div>

          <div
            className="mt-2"
            style={{ borderRadius: 10, overflow: "hidden" }}
          >
            <GoogleMap
              inputProps={{ readOnly: readOnly }}
              mapContainerStyle={containerStyle || DEFAULT_CONTAINER_STYLE}
              center={mapCenter}
              zoom={zoom}
              version="weekly"
              options={mapOptions}
              onClick={handleMapClick}
            >
              {hasMarker && (
                <div>
                  <MarkerF
                    position={{ lat: marker.lat, lng: marker.lng }}
                    draggable={draggable && !readOnly}
                    onDragEnd={handleMarkerDragEnd}
                  />
                </div>
              )}
            </GoogleMap>
          </div>
        </>
      )}
    </div>
  );
}

AutocompleteMap.propTypes = {
  onMarkerChange: PropTypes.func.isRequired,
  marker: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    formattedAddress: PropTypes.string,
    city: PropTypes.string,
    postalCode: PropTypes.string,
    address: PropTypes.string,
  }),
  error: PropTypes.bool,
  readOnly: PropTypes.bool,
  draggable: PropTypes.bool,
  containerStyle: PropTypes.object,
  inputFieldLabel: PropTypes.string,
  required: PropTypes.bool,
};

export default AutocompleteMap;
